import { z } from 'zod';
import { AlertChannel, AlertEvent, AlertRule } from './alert';
import { DbEvent, DbSnapshot } from './db';
import { InstancePresence } from './presence';
import { ProfileMeta } from './profile';
import { ReceivedSnapshot } from './snapshot';
import { SpanRecord } from './span-record';
import { TxSummary } from './tx-summary';
import { WorkerAggregate } from './worker-aggregate';

/**
 * 내부 조회 API의 응답 봉투 (17회차, 감사 C-8 = 계약 공백 L5·L6).
 *
 * 16회차까지 `TxSearchResult`·`TxDetail`·`DbRecentResult`·`DbEventsResult`·`AlertsResult`·
 * `ActiveAlertsResult`·`AlertRulesResult`가 **서버와 콘솔에 두 벌씩** 있었다. 서버는
 * `duckdb.service.ts`·`dbwatch.controller.ts`·`alert.controller.ts`·`query.controller.ts`에,
 * 콘솔은 `search/query.ts`·`useDbEvents.ts`·`useAlerts.ts`·`useTxDetail.ts`에 각각 적혀 있어
 * 실제로 어긋나 있었다 — 서버가 주는 `truncated`가 콘솔 쪽 인터페이스에는 아예 없었다.
 * `/api/rt/recent`·`/api/tx/recent`는 한술 더 떠 서버에도 이름이 없는 인라인 리터럴이었다.
 *
 * 계약을 여기 하나만 둔다. 서버 컨트롤러는 반환형으로 이 타입을 쓰고(`Promise<TxSearchResult>`),
 * 콘솔은 `as T` 캐스팅 대신 이 스키마로 `parse`한다. 한쪽만 고치면 타입 단계에서 걸린다.
 *
 * 선례는 `storage.ts`의 `StorageStatus`다. 저장소 상태 API는 처음부터 이렇게 계약에 있었다.
 * 밖으로 내놓는 계약(`external.ts`)과 달리 이것들은 **내부 API**라 버전 필드를 두지 않는다.
 * 콘솔과 서버는 항상 같이 배포된다.
 *
 * node 전용 코드를 넣지 않는다. 콘솔이 `customConditions: ["source"]`로 이 파일을 소스째
 * 번들하므로 `node:*` import 하나가 브라우저 빌드를 깬다.
 */

const count = z.number().int().nonnegative();
const epochMs = z.number().int().nonnegative();

/**
 * 날짜 폴더를 여는 조회가 공통으로 싣는 두 필드 (감사 D-2).
 * `days`는 이번 조회가 실제로 연 폴더 수, `truncated`는 상한에 걸려 오래된 쪽을 못 본 것이다.
 * 참이면 `total`도 본 범위 안의 값이므로 화면은 기간을 좁히라고 알려야 한다.
 */
const scanned = {
  /** 이번 조회가 연 날짜 폴더 수. 진단용 */
  days: count,
  /** 날짜 폴더 상한에 걸려 오래된 쪽을 못 봤다 */
  truncated: z.boolean().optional(),
};

// ---- 요청 조회 ----

/**
 * `GET /api/tx/search`의 정렬 (22-B회차, 빠진 정보 2). 기본은 최신순이고,
 * `durationMs`는 느린 것부터다. 느린 순은 커서 페이징이 없어 첫 쪽만 온다.
 *
 * `startedAtAsc`는 **오래된 것부터**다 (23-A회차 리뷰 5). 상세의 "같은 사용자의 앞뒤 요청"이
 * 현재 요청 **뒤쪽** 창에서 가장 가까운 것을 받으려면 이 방향이 있어야 한다 — 최신순으로 받으면
 * 그 창에서 가장 먼 것부터 온다. 이쪽도 커서 페이징 없이 첫 쪽만 준다
 */
export const TX_SEARCH_SORTS = ['startedAt', 'startedAtAsc', 'durationMs'] as const;
export const TxSearchSort = z.enum(TX_SEARCH_SORTS);
export type TxSearchSort = z.infer<typeof TxSearchSort>;

/** `GET /api/tx/search` 응답 */
export const TxSearchResult = z.object({
  rows: z.array(TxSummary),
  /** 조건에 맞는 전체 건수. 페이지가 아니라 전체다 */
  total: count,
  /** 다음 페이지가 있으면 실린다. 화면은 그대로 돌려주기만 하면 된다 */
  nextCursor: z.string().optional(),
  ...scanned,
});
export type TxSearchResult = z.infer<typeof TxSearchResult>;

/**
 * 같은 trace의 **다른** 트랜잭션 한 줄 (26회차).
 *
 * A가 B를 부르면 `traceparent`가 전파돼 두 서비스에 각각 트랜잭션이 생긴다(26회차 전에는
 * 하나로 합쳐졌다). 상세 화면에서 서로 건너갈 수 있도록 같은 traceId의 다른 트랜잭션을
 * 함께 준다. 지금 보고 있는 트랜잭션 자신은 빠져 있다. 41회차부터는 **같은 서비스**의 다른
 * 줄도 여기 온다(한 trace의 root 둘).
 */
export const RelatedTx = z.object({
  service: z.string().min(1),
  /**
   * 그 트랜잭션의 id(= root span id). 상세를 `GET /api/tx/:traceId?service=&txId=`로 열면
   * 그 줄이 열린다 (D41-5)
   */
  txId: z.string().regex(/^[0-9a-f]{16}$/),
  entry: z.string().min(1),
  durationMs: z.number().nonnegative(),
});
export type RelatedTx = z.infer<typeof RelatedTx>;

/** `GET /api/tx/:traceId` 응답 */
export const TxDetail = z.object({
  summary: TxSummary,
  spans: z.array(SpanRecord),
  /** 메모리 피드에서 답했는지. 저장 파일 회전 전 요청을 볼 때 참이다 */
  fromMemory: z.boolean(),
  /**
   * 같은 traceId의 다른 서비스 트랜잭션 (26회차). 서비스가 하나면 늘 빈 배열이다.
   * 26회차 전 서버의 응답에는 없어 기본값을 둔다
   */
  related: z.array(RelatedTx).default([]),
  /**
   * 파일 수 상한(`APM_QUERY_MAX_FILES`)에 걸려 요약·span·같은 trace 줄 중 일부 파일을 못 열었다
   * (49회차, D42-10). 검색·통계의 `truncated`와 같은 뜻이다. 메모리에서만 답했으면 거짓이다.
   * 옛 서버 응답에는 없다
   */
  truncated: z.boolean().optional(),
});
export type TxDetail = z.infer<typeof TxDetail>;

/** `GET /api/tx/recent` 응답. X-View 초기 로딩용 */
export const TxRecentResult = z.object({
  /** 되돌아본 시간(분). 요청한 값을 그대로 돌려준다 */
  minutes: z.number().positive(),
  count,
  rows: z.array(TxSummary),
  /**
   * 행 수 상한(`APM_TX_RECENT_MAX_ROWS`)에 걸려 오래된 행을 빼고 최근 것만 실었다 (61회차).
   * 그 전 서버의 응답에는 없다
   */
  truncated: z.boolean().default(false),
});
export type TxRecentResult = z.infer<typeof TxRecentResult>;

/** `GET /api/rt/recent` 응답. 실시간 화면 초기 로딩용 */
export const RtRecentResult = z.object({
  minutes: z.number().positive(),
  count,
  snapshots: z.array(ReceivedSnapshot),
  /**
   * 지금 알고 있는 인스턴스와 그 생존 (26회차). 스냅샷을 안 보내는 "트레이스만" 인스턴스도
   * 여기 있다. 26회차 전 서버의 응답에는 없어 기본값을 둔다
   */
  instances: z.array(InstancePresence).default([]),
  /**
   * 스냅샷 수 상한(`APM_RT_RECENT_MAX_SNAPSHOTS`)에 걸려 오래된 스냅샷을 빼고 최근 것만 실었다 (61회차).
   * 그 전 서버의 응답에는 없다
   */
  truncated: z.boolean().default(false),
});
export type RtRecentResult = z.infer<typeof RtRecentResult>;

/**
 * `GET /api/rt/traffic`의 1분 구간 하나 (34회차).
 *
 * 끝난 요청 피드를 서버가 1분 단위로 접은 값이다. 어느 구간에 들어가는지는 **요청이 끝난
 * 시각**이 정한다(`startedAt + durationMs`). sse는 뺀다(2026-09-07 결정 4).
 */
export const TrafficBucket = z.object({
  /** 구간 시작 시각. `floor(끝난 시각 / bucketMs) * bucketMs` */
  bucketStart: epochMs,
  /**
   * 이 구간에서 값을 센 초 수. 흐르는 중인 마지막 구간은 60보다 작다.
   * 초당 값은 이것으로 나눈 것이라 마지막 구간도 앞 구간과 견줄 수 있다
   */
  seconds: z.number().positive(),
  /** 이 구간에 끝난 요청 수 */
  requests: count,
  /** 그중 오류 */
  errors: count,
  requestsPerSec: z.number().nonnegative(),
  errorsPerSec: z.number().nonnegative(),
  /**
   * 그 구간 응답시간의 느린 5% 자리.
   *
   * **근사값이다.** 서버는 한 구간·한 인스턴스마다 응답시간 표본을 256개까지만 들고,
   * 그보다 많이 오면 무작위로 갈아 끼운다. 그 구간이 256건을 넘지 않으면 정확한 값이다
   */
  p95Ms: z.number().nonnegative(),
});
export type TrafficBucket = z.infer<typeof TrafficBucket>;

/**
 * `GET /api/rt/traffic` 응답 (34회차).
 *
 * **트레이스만 서비스의 실시간 화면**이 읽는다. 1초 스냅샷이 오는 서비스는 스냅샷의
 * `rate`로 같은 그림을 이미 그리므로 이 조회를 쓰지 않는다 — 한 그림에 소스가 둘이 되면
 * 두 값이 어긋났을 때 어느 쪽이 맞는지 말할 수 없다.
 */
export const TrafficResult = z.object({
  at: epochMs,
  /** 구간 길이(ms). 지금은 60,000 고정이다 */
  bucketMs: z.number().int().positive(),
  /** 되돌아본 시간(분). 요청한 값을 그대로 돌려준다 */
  minutes: z.number().positive(),
  /** 값이 있는 구간만. 시각 오름차순이고 조용했던 구간은 아예 없다 */
  rows: z.array(TrafficBucket),
});
export type TrafficResult = z.infer<typeof TrafficResult>;

/** `GET /api/workers` 응답. 워커는 개별 요약 행이 없다 (2026-09-07 결정 1) */
export const WorkerResult = z.object({
  rows: z.array(WorkerAggregate),
  total: count,
  ...scanned,
});
export type WorkerResult = z.infer<typeof WorkerResult>;

/**
 * `GET /api/profiles` 응답. 봉투 없이 배열 그대로다 (8회차 결정 4).
 * 다른 조회와 모양이 다르지만 지금 바꾸면 화면까지 같이 고쳐야 해 그대로 둔다.
 */
export const ProfileListResult = z.array(ProfileMeta);
export type ProfileListResult = z.infer<typeof ProfileListResult>;

// ---- DB 워처 ----

/** 워처 자신의 상태. `GET /api/db/recent`가 스냅샷과 함께 준다 (9회차 결정 2) */
export const DbWatchStats = z.object({
  enabled: z.boolean(),
  connected: z.boolean(),
  database: z.string().optional(),
  /** 수집을 돈 횟수 */
  ticks: count,
  /** 쿼리 실패·접속 실패 횟수 */
  errors: count,
  reconnects: count,
  /** 확정해 저장한 사건 수 */
  events: count,
  /** 아직 안 끝난 사건 수 */
  openEvents: count,
  /** 수집 한 번(쿼리 2개)에 걸린 평균 ms. 워처가 대상 DB에 주는 부하의 실측값이다 */
  avgQueryMs: z.number().nonnegative(),
  lastQueryMs: z.number().nonnegative(),
  /** 마지막 스냅샷의 매칭률 */
  matchRate: z.number(),
  /** 대상 DB의 `track_activity_query_size` */
  trackActivityQuerySize: count,
  /** 스냅샷 링버퍼가 들고 있는 추정 바이트(JSON 길이 합). 그 전 서버의 응답에는 없다 */
  bytes: count.default(0),
  /** 바이트 예산(`APM_DBWATCH_RING_MAX_BYTES`)에 걸려 버린 스냅샷의 추정 바이트 누적 */
  evictedBytes: count.default(0),
});
export type DbWatchStats = z.infer<typeof DbWatchStats>;

/** `GET /api/db/recent` 응답 */
export const DbRecentResult = z.object({
  minutes: z.number().positive(),
  count,
  snapshots: z.array(DbSnapshot),
  stats: DbWatchStats,
});
export type DbRecentResult = z.infer<typeof DbRecentResult>;

/** `GET /api/db/events` 응답 */
export const DbEventsResult = z.object({
  rows: z.array(DbEvent),
  total: count,
  ...scanned,
});
export type DbEventsResult = z.infer<typeof DbEventsResult>;

// ---- 알림 ----

/** 규칙 평가 엔진의 카운터 (10회차) */
export const AlertEngineStats = z.object({
  enabled: z.boolean(),
  rules: count,
  /** 지금 발화 중인 규칙×키 수 */
  firing: count,
  fired: count,
  resolved: count,
  /** 쿨다운에 걸려 안 보낸 수 */
  cooldownSuppressed: count,
  /** 분당 상한에 걸려 안 보낸 수 */
  rateSuppressed: count,
  /** 너무 오래된 스냅샷이라 평가하지 않은 수 */
  staleSkipped: count,
  /**
   * 힙 상한(`heap.limitBytes`)을 안 보내는 옛 에이전트라 힙 규칙을 건너뛴 수 (24회차).
   * 0이 아니면 그 인스턴스에서 힙 감시가 비어 있다는 뜻이다
   */
  heapLimitMissing: count.default(0),
  /**
   * 규칙당 키 상한을 넘겨 버린 상태 수 (리뷰 R26-09).
   *
   * 잘못된 인스턴스 이름처럼 값이 계속 새로 생기면 규칙 하나의 상태 Map이 끝없이 커진다.
   * 0이 아니면 그 규칙의 `keyBy`가 카디널리티가 너무 높은 값으로 쪼개고 있다는 뜻이다
   */
  keysEvicted: count.default(0),
  evaluatedAt: epochMs,
});
export type AlertEngineStats = z.infer<typeof AlertEngineStats>;

/** 웹훅 채널 하나의 카운터 */
export const WebhookStats = z.object({
  sent: count,
  failed: count,
  /** 주소가 없어 그냥 지나간 수 */
  skipped: count,
});
export type WebhookStats = z.infer<typeof WebhookStats>;

/** OneWorks2 지식 문서 채널 하나의 카운터 (16회차) */
export const OneWorksStats = z.object({
  /** 새로 만든 문서 수(발화) */
  created: count,
  /** 갱신한 문서 수(해제) */
  updated: count,
  /** 두 번 시도하고도 실패한 수 */
  failed: count,
  /** 설정이 비어 그냥 지나간 수 */
  skipped: count,
});
export type OneWorksStats = z.infer<typeof OneWorksStats>;

/**
 * 메일·텔레그램 채널 하나의 카운터 (53회차). 세는 단위는 **받는 사람 한 명**이다 —
 * 알림 한 건이 담당자 셋에게 가면 `sent`가 셋 오른다.
 */
export const RecipientChannelStats = z.object({
  /** 받는 쪽이 받았다고 답한 수 */
  sent: count,
  /** 두 번 시도하고도 실패한 수 */
  failed: count,
  /** 보내는 쪽 설정(`.env`)이 비어 그냥 지나간 수 */
  skipped: count,
  /** 담당 서비스·심각도에 맞는 담당자가 없어 아무에게도 안 간 알림 수 */
  noRecipient: count,
});
export type RecipientChannelStats = z.infer<typeof RecipientChannelStats>;

/** 발송기의 채널별 카운터. 토큰 만료가 `failed`로 드러난다 */
export const DispatchStats = z.object({
  channels: z.array(z.string()),
  /** 채널로 나간 건수 */
  sent: count,
  /** 규칙이 모르는 채널을 가리켜 못 보낸 건수 */
  unknown: count,
  /**
   * 채널별 실패의 합 (24회차 리뷰 11). `AlertEvent.notified`는 "쿨다운·분당 상한을 통과해
   * 보내기로 했다"까지만 말한다. 실제로 밖에 닿았는지는 이 값과 채널별 카운터가 말한다
   */
  failed: count.default(0),
  webhooks: z.record(z.string(), WebhookStats),
  oneworks: z.record(z.string(), OneWorksStats),
  /** 메일 채널 (53회차). 칸이 없는 옛 서버 응답은 빈 표다 */
  mail: z.record(z.string(), RecipientChannelStats).default({}),
  /** 텔레그램 채널 (53회차). 칸이 없는 옛 서버 응답은 빈 표다 */
  telegram: z.record(z.string(), RecipientChannelStats).default({}),
});
export type DispatchStats = z.infer<typeof DispatchStats>;

/** `GET /api/alerts` 응답. 발화·복구를 합친 뒤의 건수다 */
export const AlertsResult = z.object({
  rows: z.array(AlertEvent),
  total: count,
  ...scanned,
});
export type AlertsResult = z.infer<typeof AlertsResult>;

/** `GET /api/alerts/active` 응답 */
export const ActiveAlertsResult = z.object({
  rows: z.array(AlertEvent),
  stats: AlertEngineStats,
});
export type ActiveAlertsResult = z.infer<typeof ActiveAlertsResult>;

/** `GET /api/alerts/rules` 응답. 지금 물고 있는 규칙과 채널, 버린 규칙, 발송 카운터 */
export const AlertRulesResult = z.object({
  /** 규칙 파일 경로 */
  file: z.string(),
  loadedAt: epochMs,
  rules: z.array(AlertRule),
  channels: z.array(AlertChannel),
  /** 버린 규칙과 그 이유 */
  problems: z.array(z.string()),
  /** 규칙 파일에 쓸 수 있는 신호 목록 */
  signals: z.array(z.string()),
  stats: AlertEngineStats,
  dispatch: DispatchStats,
});
export type AlertRulesResult = z.infer<typeof AlertRulesResult>;

/** 규칙 파일 전문. 화면 편집기가 이것을 열어 고친다 (24회차) */
export const AlertRulesFileResult = z.object({
  /** 규칙 파일 경로. 비어 있으면 알림 엔진이 꺼져 있다 */
  file: z.string(),
  /** 파일 내용 그대로. 주석·순서가 다 살아 있다 */
  text: z.string(),
  /**
   * 이 판의 sha256(16진수 64자). 저장할 때 그대로 돌려보내면 서버가 그 사이에 파일이
   * 바뀌지 않았는지 본다 (24회차 리뷰 5). 파일을 못 읽었으면 빈 문자열의 해시다
   */
  digest: z.string().regex(/^[0-9a-f]{64}$/),
  /** 지금 물고 있는 규칙을 읽은 시각 */
  loadedAt: epochMs,
  /** 파일을 읽을 수 있었나. false면 `text`가 비어 있다 */
  readable: z.boolean(),
});
export type AlertRulesFileResult = z.infer<typeof AlertRulesFileResult>;

/**
 * 규칙 파일 편집 요청 본문. yml 전문을 그대로 보낸다 (주석을 지키려는 것이다).
 *
 * `digest`는 **저장(PUT)에만** 필요하다. 편집기가 연 판의 해시이고, 그 사이 파일이 바뀌었으면
 * 서버가 409로 거절한다 — 두 사람이 같은 판을 열어 차례로 저장하면 앞엣것이 조용히 사라지기
 * 때문이다 (24회차 리뷰 5). 검증(`/check`)은 파일을 건드리지 않아 필요 없다.
 */
export const AlertRulesUpdate = z.object({
  text: z.string().max(256 * 1024),
  digest: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
});
export type AlertRulesUpdate = z.infer<typeof AlertRulesUpdate>;

/**
 * 규칙 파일 검증·저장 결과 (24회차).
 *
 * 검증(`POST /api/alerts/rules/check`)과 저장(`PUT /api/alerts/rules`)이 같은 모양을 낸다.
 * 화면은 두 단추가 같은 상자를 채우면 되고, 저장 여부만 `saved`로 갈린다.
 */
export const AlertRulesCheckResult = z.object({
  /** 이 본문으로 규칙을 세울 수 있나. false면 저장하지 않았다 */
  ok: z.boolean(),
  /** 파일을 통째로 못 읽은 이유. `ok`가 false일 때만 있다 */
  error: z.string().optional(),
  /** 버린 규칙과 그 이유. `ok`가 true여도 있을 수 있다 */
  problems: z.array(z.string()),
  /** 이 본문이 세우는 규칙·채널 수 */
  rules: count,
  channels: count,
  /** 적용 전에 물고 있던 규칙 수. 화면이 전후를 나란히 보여 준다 */
  before: count,
  /** 파일에 적었나. 검증만 한 요청은 false */
  saved: z.boolean(),
  /** 직전 파일을 남긴 이름. 저장했을 때만 있다 */
  backup: z.string().optional(),
  /** 저장한 판(또는 검증한 본문)의 sha256. 화면이 다음 저장에 그대로 실어 보낸다 */
  digest: z.string().optional(),
});
export type AlertRulesCheckResult = z.infer<typeof AlertRulesCheckResult>;

// ---- 외부 연동 토큰 ----

/**
 * 외부 연동 토큰 하나 (23-B회차).
 *
 * **평문도 해시도 여기 실리지 않는다.** 평문은 발급 응답에 딱 한 번 나가고, 해시는 서버의
 * `tokens.json`에만 있다. 목록에 해시를 실으면 화면을 여는 사람이 오프라인 대입을 해 볼 수 있다.
 */
export const ExternalToken = z.object({
  /** 폐기할 때 쓰는 식별자. 토큰 값과 무관한 난수다 */
  id: z.string(),
  /** 소비자 이름. 누구에게 준 토큰인지 사람이 알아보게 하는 유일한 단서다 */
  name: z.string(),
  createdAt: epochMs,
  /** 이 토큰으로 외부 API가 마지막으로 통과한 시각. 한 번도 안 썼으면 없다 */
  lastUsedAt: epochMs.optional(),
  /** 폐기한 시각. 있으면 이 토큰은 더 통하지 않는다 */
  revokedAt: epochMs.optional(),
  /** 발급을 요청한 곳의 주소. 콘솔에 로그인이 없어 이것이 유일한 흔적이다 */
  createdBy: z.string().optional(),
});
export type ExternalToken = z.infer<typeof ExternalToken>;

/** `GET /api/external/tokens`·`DELETE /api/external/tokens/:id` 응답 */
export const ExternalTokensResult = z.object({
  /** 폐기한 것까지 전부. 만든 순서의 역순이다 */
  tokens: z.array(ExternalToken),
  /** `.env`의 `APM_EXTERNAL_TOKEN`이 설정돼 있는지. 값은 내지 않는다 */
  bootstrap: z.boolean(),
  /**
   * 저장 파일이 깨져 발급·폐기가 막혀 있다 (23-B 리뷰 4). 참이면 조회만 되고 바꾸려 하면 503이다.
   * 정상일 때는 실리지 않는다
   */
  blocked: z.boolean().optional(),
});
export type ExternalTokensResult = z.infer<typeof ExternalTokensResult>;

/** `POST /api/external/tokens` 요청 */
export const ExternalTokenCreate = z.object({
  /** 소비자 이름. 빈 이름은 400이다 */
  name: z.string().trim().min(1).max(80),
});
export type ExternalTokenCreate = z.infer<typeof ExternalTokenCreate>;

/** `POST /api/external/tokens` 응답. `value`는 이 응답에만 실린다 */
export const ExternalTokenCreated = z.object({
  token: ExternalToken,
  /** 평문 토큰. 서버는 해시만 갖고 있어 다시 낼 수 없다 */
  value: z.string(),
});
export type ExternalTokenCreated = z.infer<typeof ExternalTokenCreated>;
